/**
 * WeaveIn.ai Platform - Form Instances Hook
 * React hook for form instance management and submission
 */

import { useState, useEffect, useCallback } from 'react';
import {
  formsAPI,
  fetchAllPages,
  FormInstance,
  FormInstanceCreate,
  FormSubmissionData,
  FormInstanceStatus,
} from '@/services/api/forms';

interface UseFormInstancesOptions {
  enrollment_id?: number;
  form_template_id?: number;
  status?: FormInstanceStatus;
  assigned_to_user_id?: number;
  autoFetch?: boolean;
  /** Rows per request (backend caps at 100; default 50). */
  page_size?: number;
  /** Walk every page instead of stopping at the first (the silent 50-row cap). */
  fetchAll?: boolean;
}

interface UseFormInstancesReturn {
  instances: FormInstance[];
  loading: boolean;
  error: string | null;
  createInstance: (data: FormInstanceCreate) => Promise<FormInstance | null>;
  getInstance: (id: number) => Promise<FormInstance | null>;
  submitInstance: (id: number, data: FormSubmissionData) => Promise<any>;
  deleteInstance: (id: number) => Promise<boolean>;
  getHistory: (id: number) => Promise<any[]>;
  refetch: () => Promise<void>;
}

export default function useFormInstances(
  options: UseFormInstancesOptions = {}
): UseFormInstancesReturn {
  const {
    enrollment_id,
    form_template_id,
    status,
    assigned_to_user_id,
    autoFetch = true,
    page_size,
    fetchAll = false,
  } = options;

  const [instances, setInstances] = useState<FormInstance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInstances = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = { enrollment_id, form_template_id, status, assigned_to_user_id };
      const data = fetchAll
        ? await fetchAllPages((page, size) =>
            formsAPI.listFormInstances({ ...params, page, page_size: size }),
          )
        : await formsAPI.listFormInstances({ ...params, page_size });
      setInstances(Array.isArray(data) ? data : []);
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch form instances';
      setError(errorMessage);
      console.error('Error fetching form instances:', err);
    } finally {
      setLoading(false);
    }
  }, [enrollment_id, form_template_id, status, assigned_to_user_id, page_size, fetchAll]);

  useEffect(() => {
    if (autoFetch) {
      fetchInstances();
    }
  }, [autoFetch, fetchInstances]);

  const createInstance = async (data: FormInstanceCreate): Promise<FormInstance | null> => {
    setLoading(true);
    setError(null);

    try {
      const newInstance = await formsAPI.createFormInstance(data);
      setInstances((prev) => [newInstance, ...prev]);
      return newInstance;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to create form instance';
      setError(errorMessage);
      console.error('Error creating form instance:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getInstance = async (id: number): Promise<FormInstance | null> => {
    setLoading(true);
    setError(null);

    try {
      const instance = await formsAPI.getFormInstance(id);
      return instance;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to fetch form instance';
      setError(errorMessage);
      console.error('Error fetching form instance:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const submitInstance = async (id: number, data: FormSubmissionData): Promise<any> => {
    setLoading(true);
    setError(null);

    try {
      const result = await formsAPI.submitFormInstance(id, data);
      // Update local state
      await fetchInstances();
      return result;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to submit form';
      setError(errorMessage);
      console.error('Error submitting form:', err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteInstance = async (id: number): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      await formsAPI.deleteFormInstance(id);
      setInstances((prev) => prev.filter((i) => i.id !== id));
      return true;
    } catch (err: any) {
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to delete form instance';
      setError(errorMessage);
      console.error('Error deleting form instance:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const getHistory = async (id: number): Promise<any[]> => {
    try {
      const history = await formsAPI.getFormInstanceHistory(id);
      return history;
    } catch (err: any) {
      console.error('Error fetching form history:', err);
      return [];
    }
  };

  return {
    instances,
    loading,
    error,
    createInstance,
    getInstance,
    submitInstance,
    deleteInstance,
    getHistory,
    refetch: fetchInstances,
  };
}
